import { useEffect, useState } from 'react';
import { Alert, App, Form, Input, Modal, Select } from 'antd';
import { useAdminApi } from '../admin-api-context.js';
import type { UnknownOutcomeResolution } from '../types.js';

type ReviewInput = {
  resolution: UnknownOutcomeResolution;
  evidenceReference: string;
  reviewSummary: string;
};

export function UnknownOutcomeReviewModal({
  jobId,
  onClose,
  onReviewed,
}: {
  jobId: string | undefined;
  onClose: () => void;
  onReviewed: () => void;
}) {
  const api = useAdminApi();
  const { message } = App.useApp();
  const [form] = Form.useForm<ReviewInput>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!jobId) form.resetFields();
  }, [form, jobId]);

  async function submit(values: ReviewInput): Promise<void> {
    if (!jobId) return;
    setSaving(true);
    try {
      await api.send('POST', `/admin/v1/unknown-outcomes/${jobId}/review`, {
        ...values,
        evidenceReference: values.evidenceReference.trim(),
        reviewSummary: values.reviewSummary.trim(),
      });
      await message.success('Unknown outcome review recorded');
      form.resetFields();
      onReviewed();
    } catch (error) {
      await message.error(
        error instanceof Error ? error.message : 'Review could not be saved',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={Boolean(jobId)}
      title="Review unknown provider outcome"
      okText="Record immutable review"
      confirmLoading={saving}
      onCancel={onClose}
      onOk={() => form.submit()}
    >
      <Alert
        type="warning"
        showIcon
        message="Never replay this job locally"
        description="This review records the verified provider outcome; it does not authorize replay. Do not paste prompts, user content, secrets, or personal data."
      />
      <Form<ReviewInput>
        form={form}
        layout="vertical"
        onFinish={(values) => void submit(values)}
        initialValues={{ resolution: 'PROVIDER_EFFECT_CONFIRMED' }}
        style={{ marginTop: 16 }}
      >
        <Form.Item
          name="resolution"
          label="Verified outcome"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              {
                value: 'PROVIDER_EFFECT_CONFIRMED',
                label: 'Provider effect confirmed',
              },
              {
                value: 'NO_PROVIDER_EFFECT_CONFIRMED',
                label: 'No provider effect confirmed',
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="evidenceReference"
          label="Evidence reference"
          extra="Use an incident, provider-log, or change-ticket identifier—not evidence content."
          rules={[
            { required: true, min: 3, max: 200 },
            {
              pattern: /^[A-Za-z0-9][A-Za-z0-9._:/#-]*$/,
              message:
                'Use letters, numbers, dots, colons, slashes, hashes, or dashes',
            },
          ]}
        >
          <Input placeholder="INC-2026-1042" />
        </Form.Item>
        <Form.Item
          name="reviewSummary"
          label="Operational review summary"
          rules={[{ required: true, min: 20, max: 2000 }]}
        >
          <Input.TextArea rows={5} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
